import React, { useState } from 'react';
import { LuUpload, LuX, LuCheck, LuInfo, LuFileText, LuDownload } from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';
import LoadingSpinner from '../common/LoadingSpinner';
import { csvImportService } from '../../services/csvImportService';

const ImportCsvModal = ({ isOpen, onClose, entityType, onImportComplete }) => {
    const [file, setFile] = useState(null);
    const [csvData, setCsvData] = useState(null);
    const [validation, setValidation] = useState(null);
    const [duplicates, setDuplicates] = useState(null);
    const [updateOptions, setUpdateOptions] = useState({});
    const [importing, setImporting] = useState(false);
    const [importResults, setImportResults] = useState(null);
    const [step, setStep] = useState('upload'); // upload, preview, duplicates, importing, results
    const [dragActive, setDragActive] = useState(false);

    const isAccounts = entityType === 'accounts';
    const isLeads = entityType === 'leads';
    const entityName = isAccounts ? 'Accounts' : (isLeads ? 'Leads' : 'Contacts');
    const exampleFile = isAccounts ? '/examples/accounts_example.csv' : (isLeads ? '/examples/leads_example.csv' : '/examples/contacts_example.csv');

    // Reset modal state
    const resetModal = () => {
        setFile(null);
        setCsvData(null);
        setValidation(null);
        setDuplicates(null);
        setUpdateOptions({});
        setImporting(false);
        setImportResults(null);
        setStep('upload');
    };

    const handleClose = () => {
        resetModal();
        onClose();
    };

    // Handle file selection
    const handleFileChange = async (selectedFile) => {
        if (!selectedFile) return;

        if (!selectedFile.name.endsWith('.csv')) {
            alert('Please select a CSV file');
            return;
        }

        setFile(selectedFile);

        try {
            // Parse CSV
            const data = await csvImportService.parseCSV(selectedFile);
            setCsvData(data);

            // Validate
            const validationResult = isAccounts
                ? csvImportService.validateAccountsCSV(data)
                : (isLeads ? csvImportService.validateLeadsCSV(data) : csvImportService.validateContactsCSV(data));

            setValidation(validationResult);

            if (validationResult.valid) {
                setStep('preview');
            } else {
                setStep('upload');
            }
        } catch (error) {
            alert(`Error parsing CSV: ${error.message}`);
            setFile(null);
        }
    };

    // Handle drag and drop
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    // Check for duplicates
    const handleCheckDuplicates = async () => {
        setImporting(true);
        try {
            if (isLeads) {
                // For leads, we skip duplicate check UI and proceed directly to import
                // But to keep flow consistent, we can just say "no duplicates" here and let user click Import
                // OR we can auto-trigger - let's just make it return empty duplicates
                setDuplicates({ hasDuplicates: false, duplicates: [] });
                // We'll skip the UI step for duplicates in the render
                handleImport({});
                return;
            }

            const result = isAccounts
                ? await csvImportService.detectAccountDuplicates(csvData)
                : await csvImportService.detectContactDuplicates(csvData);

            if (result.success) {
                setDuplicates(result);

                if (result.hasDuplicates) {
                    // Initialize update options - default to skip
                    const options = {};
                    result.duplicates.forEach(dup => {
                        options[dup.rowIndex] = false; // false = skip, true = update
                    });
                    setUpdateOptions(options);
                    setStep('duplicates');
                } else {
                    // No duplicates, proceed to import
                    await handleImport({});
                }
            } else {
                alert(`Error checking duplicates: ${result.error}`);
            }
        } catch (error) {
            alert(`Error: ${error.message}`);
        } finally {
            setImporting(false);
        }
    };

    // Handle import
    const handleImport = async (options = updateOptions) => {
        setImporting(true);
        setStep('importing');

        try {
            const result = isAccounts
                ? await csvImportService.importAccounts(csvData, options)
                : (isLeads ? await csvImportService.importLeads(csvData) : await csvImportService.importContacts(csvData, options));

            setImportResults(result);
            setStep('results');

            if (onImportComplete) {
                onImportComplete(result);
            }
        } catch (error) {
            alert(`Import failed: ${error.message}`);
        } finally {
            setImporting(false);
        }
    };

    // Toggle update option for a duplicate
    const toggleUpdateOption = (rowIndex) => {
        setUpdateOptions(prev => ({
            ...prev,
            [rowIndex]: !prev[rowIndex]
        }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Import {entityName}</h2>
                        <p className="text-sm text-gray-600 mt-1">Upload a CSV file to import multiple {entityName.toLowerCase()}</p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <SafeIcon icon={LuX} className="h-6 w-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Step 1: Upload */}
                    {step === 'upload' && (
                        <div className="space-y-4">
                            {/* Download example */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <SafeIcon icon={LuFileText} className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                                    <div className="flex-1">
                                        <h3 className="text-sm font-medium text-blue-900">Need a template?</h3>
                                        <p className="text-sm text-blue-700 mt-1">Download our example CSV file to see the required format.</p>
                                        <a
                                            href={exampleFile}
                                            download
                                            className="inline-flex items-center mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
                                        >
                                            <SafeIcon icon={LuDownload} className="h-4 w-4 mr-1" />
                                            Download Example CSV
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* Upload area */}
                            <div
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-300 hover:border-gray-400'
                                    }`}
                            >
                                <SafeIcon icon={LuUpload} className="mx-auto h-12 w-12 text-gray-400" />
                                <p className="mt-2 text-sm text-gray-600">
                                    Drag and drop your CSV file here, or
                                </p>
                                <label className="mt-2 inline-block">
                                    <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                                        Choose File
                                    </span>
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={(e) => handleFileChange(e.target.files[0])}
                                        className="hidden"
                                    />
                                </label>
                                {file && (
                                    <p className="mt-3 text-sm text-gray-700">
                                        Selected: <span className="font-medium">{file.name}</span>
                                    </p>
                                )}
                            </div>

                            {/* Validation errors */}
                            {validation && !validation.valid && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <SafeIcon icon={LuInfo} className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
                                        <div>
                                            <h3 className="text-sm font-medium text-red-900">Validation Errors</h3>
                                            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                                                {validation.errors.map((error, idx) => (
                                                    <li key={idx}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Preview */}
                    {step === 'preview' && csvData && (
                        <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <SafeIcon icon={LuCheck} className="h-5 w-5 text-green-600 mr-3" />
                                    <div>
                                        <h3 className="text-sm font-medium text-green-900">CSV Validated Successfully</h3>
                                        <p className="text-sm text-green-700 mt-1">
                                            Found {csvData.length} {csvData.length === 1 ? 'record' : 'records'} to import
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Preview table */}
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                {Object.keys(csvData[0]).map((header) => (
                                                    <th
                                                        key={header}
                                                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                                    >
                                                        {header}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {csvData.slice(0, 10).map((row, idx) => (
                                                <tr key={idx}>
                                                    {Object.values(row).map((value, colIdx) => (
                                                        <td key={colIdx} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                                            {value || '-'}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {csvData.length > 10 && (
                                    <div className="bg-gray-50 px-4 py-3 text-sm text-gray-600 text-center">
                                        Showing first 10 of {csvData.length} records
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Duplicates */}
                    {step === 'duplicates' && duplicates && duplicates.hasDuplicates && (
                        <div className="space-y-4">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <SafeIcon icon={LuInfo} className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
                                    <div>
                                        <h3 className="text-sm font-medium text-yellow-900">Duplicates Found</h3>
                                        <p className="text-sm text-yellow-700 mt-1">
                                            {duplicates.duplicates.length} duplicate {duplicates.duplicates.length === 1 ? 'record' : 'records'} found.
                                            Choose whether to update or skip each one.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Duplicates list */}
                            <div className="space-y-3">
                                {duplicates.duplicates.map((dup, idx) => (
                                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <span className="text-xs font-medium text-gray-500">Row {dup.rowIndex + 2}</span>
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {isAccounts ? dup.csvData.name : `${dup.csvData.first_name} ${dup.csvData.last_name}`}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-4 text-sm">
                                                    <div>
                                                        <p className="text-gray-500 mb-1">CSV Data:</p>
                                                        <p className="text-gray-900">
                                                            {isAccounts
                                                                ? `${dup.csvData.industry || 'N/A'}, ${dup.csvData.city || 'N/A'}`
                                                                : (!isLeads && dup.csvData.email || 'N/A')}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-gray-500 mb-1">Existing Data:</p>
                                                        <p className="text-gray-900">
                                                            {isAccounts
                                                                ? `${dup.existingData.industry || 'N/A'}, ${dup.existingData.city || 'N/A'}`
                                                                : dup.existingData.email || 'N/A'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="ml-4">
                                                <button
                                                    onClick={() => toggleUpdateOption(dup.rowIndex)}
                                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${updateOptions[dup.rowIndex]
                                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    {updateOptions[dup.rowIndex] ? 'Update' : 'Skip'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 4: Importing */}
                    {step === 'importing' && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <LoadingSpinner size="large" />
                            <p className="mt-4 text-gray-600">Importing {entityName.toLowerCase()}...</p>
                        </div>
                    )}

                    {/* Step 5: Results */}
                    {step === 'results' && importResults && (
                        <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <SafeIcon icon={LuCheck} className="h-5 w-5 text-green-600 mr-3" />
                                    <div>
                                        <h3 className="text-sm font-medium text-green-900">Import Complete</h3>
                                    </div>
                                </div>
                            </div>

                            {/* Results summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-600">Total</p>
                                    <p className="text-2xl font-bold text-gray-900">{importResults.total}</p>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <p className="text-sm text-green-600">Created</p>
                                    <p className="text-2xl font-bold text-green-900">{importResults.created}</p>
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <p className="text-sm text-blue-600">Updated</p>
                                    <p className="text-2xl font-bold text-blue-900">{importResults.updated}</p>
                                </div>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-600">Skipped</p>
                                    <p className="text-2xl font-bold text-gray-900">{importResults.skipped}</p>
                                </div>
                            </div>

                            {/* Errors */}
                            {importResults.failed > 0 && (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <div className="flex items-start">
                                        <SafeIcon icon={LuInfo} className="h-5 w-5 text-red-600 mt-0.5 mr-3" />
                                        <div className="flex-1">
                                            <h3 className="text-sm font-medium text-red-900">
                                                {importResults.failed} {importResults.failed === 1 ? 'Error' : 'Errors'}
                                            </h3>
                                            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                                                {importResults.errors.map((error, idx) => (
                                                    <li key={idx}>{error}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3">
                    {step === 'upload' && (
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                        >
                            Cancel
                        </button>
                    )}

                    {step === 'preview' && (
                        <>
                            <button
                                onClick={resetModal}
                                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                            >
                                Back
                            </button>
                            <button
                                onClick={handleCheckDuplicates}
                                disabled={importing}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {importing ? 'Checking...' : 'Continue'}
                            </button>
                        </>
                    )}

                    {step === 'duplicates' && (
                        <>
                            <button
                                onClick={() => setStep('preview')}
                                className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                            >
                                Back
                            </button>
                            <button
                                onClick={() => handleImport(updateOptions)}
                                disabled={importing}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {importing ? 'Importing...' : 'Import'}
                            </button>
                        </>
                    )}

                    {step === 'results' && (
                        <button
                            onClick={handleClose}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Done
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImportCsvModal;
